<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';

import ContactAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import { useDealStagesStore } from 'dashboard/stores/dealStages';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const emit = defineEmits(['success']);

const { t } = useI18n();
const dealsStore = useDealsStore();
const dealStagesStore = useDealStagesStore();

const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'BRL' },
  { value: 'USD', label: 'USD' },
];

const dialogRef = ref(null);
const isCreating = ref(false);
const showErrors = ref(false);
const contactOptions = ref([]);
const contactMode = ref('existing');

const form = reactive({
  contactId: '',
  contactName: '',
  title: '',
  dealStageId: '',
  value: '',
  currency: 'BRL',
});

const newContact = reactive({
  name: '',
  phone: '',
  email: '',
  cpfCnpj: '',
  address: '',
});

const stageOptions = computed(() =>
  dealStagesStore.orderedStages.map(stage => ({
    value: stage.id,
    label: stage.name,
  }))
);

const isContactInvalid = computed(
  () => showErrors.value && contactMode.value === 'existing' && !form.contactId
);
const isNewContactNameInvalid = computed(
  () =>
    showErrors.value && contactMode.value === 'new' && !newContact.name.trim()
);
const isTitleInvalid = computed(() => showErrors.value && !form.title.trim());

const resetForm = () => {
  form.contactId = '';
  form.contactName = '';
  form.title = '';
  form.dealStageId = dealStagesStore.orderedStages[0]?.id || '';
  form.value = '';
  form.currency = 'BRL';
  contactOptions.value = [];
  contactMode.value = 'existing';
  newContact.name = '';
  newContact.phone = '';
  newContact.email = '';
  newContact.cpfCnpj = '';
  newContact.address = '';
  showErrors.value = false;
};

const toggleContactMode = () => {
  contactMode.value = contactMode.value === 'existing' ? 'new' : 'existing';
  showErrors.value = false;
};

const toContactOption = contact => ({ label: contact.name, value: contact.id });

const fetchContacts = async query => {
  try {
    const {
      data: { payload },
    } = await ContactAPI.search(query, 1);
    contactOptions.value = (payload || []).map(toContactOption);
  } catch {
    contactOptions.value = [];
  }
};

const handleOpenContactCombobox = () => fetchContacts('');

const handleSearchContact = useDebounceFn(query => {
  fetchContacts(query?.trim() || '');
}, 300);

const handleSelectContact = value => {
  const id = value ? Number(value) : '';
  const selected = contactOptions.value.find(option => option.value === id);
  form.contactId = id;
  form.contactName = selected?.label || '';
  // Prefill the title from the contact so the happy path needs no typing,
  // but never overwrite something the user already edited.
  if (!form.title.trim()) form.title = form.contactName;
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  if (isCreating.value) return;

  showErrors.value = true;
  if (contactMode.value === 'new') {
    if (!newContact.name.trim() || !form.title.trim()) return;
  } else if (!form.contactId || !form.title.trim()) {
    return;
  }

  isCreating.value = true;
  try {
    let { contactId } = form;

    if (contactMode.value === 'new') {
      try {
        const customAttributes = {};
        if (newContact.cpfCnpj.trim()) {
          customAttributes.cpf_cnpj = newContact.cpfCnpj.trim();
        }
        if (newContact.address.trim()) {
          customAttributes.endereco = newContact.address.trim();
        }

        const { data } = await ContactAPI.create({
          name: newContact.name.trim(),
          ...(newContact.phone.trim()
            ? { phone_number: newContact.phone.trim() }
            : {}),
          ...(newContact.email.trim()
            ? { email: newContact.email.trim() }
            : {}),
          ...(Object.keys(customAttributes).length
            ? { custom_attributes: customAttributes }
            : {}),
        });
        const created = data.payload.contact;
        contactId = created.id;
        // Prefill the title from the new contact, same rule as an existing one.
        if (!form.title.trim()) form.title = created.name;
      } catch {
        useAlert(t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.ERROR'));
        return;
      }
    }

    await dealsStore.create({
      contactId,
      title: form.title.trim(),
      dealStageId: form.dealStageId || undefined,
      valueCents:
        form.value !== '' ? Math.round(Number(form.value) * 100) : undefined,
      currency: form.currency || undefined,
    });
    useAlert(t('CRM_FUNIL.NEW_DEAL.SUCCESS'));
    emit('success');
    closeDialog();
  } catch {
    useAlert(t('CRM_FUNIL.NEW_DEAL.ERROR'));
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ open, form, newContact, contactMode, handleConfirm });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CRM_FUNIL.NEW_DEAL.TITLE') }}
      </span>

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.NEW_DEAL.FIELDS.CONTACT') }}
          </label>
          <Button
            :label="
              contactMode === 'existing'
                ? t('CRM_FUNIL.NEW_DEAL.CREATE_CONTACT')
                : t('CRM_FUNIL.NEW_DEAL.USE_EXISTING')
            "
            variant="link"
            size="sm"
            type="button"
            :disabled="isCreating"
            @click="toggleContactMode"
          />
        </div>

        <ComboBox
          v-if="contactMode === 'existing'"
          :model-value="form.contactId"
          :options="contactOptions"
          :display-label="form.contactName"
          :placeholder="t('CRM_FUNIL.NEW_DEAL.CONTACT_PLACEHOLDER')"
          :search-placeholder="t('CRM_FUNIL.NEW_DEAL.CONTACT_PLACEHOLDER')"
          :has-error="isContactInvalid"
          :message="
            isContactInvalid ? t('CRM_FUNIL.NEW_DEAL.CONTACT_REQUIRED') : ''
          "
          use-api-results
          @open="handleOpenContactCombobox"
          @search="handleSearchContact"
          @update:model-value="handleSelectContact"
        />

        <div v-else class="flex flex-col gap-4">
          <Input
            v-model="newContact.name"
            :label="t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.NAME')"
            :message="
              isNewContactNameInvalid
                ? t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.NAME_REQUIRED')
                : ''
            "
            :message-type="isNewContactNameInvalid ? 'error' : 'info'"
            :disabled="isCreating"
          />
          <Input
            v-model="newContact.phone"
            :label="t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.PHONE')"
            :message="t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.PHONE_HINT')"
            :disabled="isCreating"
          />
          <Input
            v-model="newContact.email"
            :label="t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.EMAIL')"
            :disabled="isCreating"
          />
          <Input
            v-model="newContact.cpfCnpj"
            :label="t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.CPF_CNPJ')"
            :disabled="isCreating"
          />
          <Input
            v-model="newContact.address"
            :label="t('CRM_FUNIL.NEW_DEAL.NEW_CONTACT.ADDRESS')"
            :disabled="isCreating"
          />
        </div>
      </div>

      <Input
        v-model="form.title"
        :label="t('CRM_FUNIL.NEW_DEAL.FIELDS.DEAL_TITLE')"
        :message="isTitleInvalid ? t('CRM_FUNIL.NEW_DEAL.TITLE_REQUIRED') : ''"
        :message-type="isTitleInvalid ? 'error' : 'info'"
        :disabled="isCreating"
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CRM_FUNIL.NEW_DEAL.FIELDS.STAGE') }}
        </label>
        <Select v-model="form.dealStageId" :options="stageOptions" />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model="form.value"
          type="number"
          min="0"
          :label="t('CRM_FUNIL.NEW_DEAL.FIELDS.VALUE')"
          :disabled="isCreating"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.NEW_DEAL.FIELDS.CURRENCY') }}
          </label>
          <Select v-model="form.currency" :options="CURRENCY_OPTIONS" />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('CRM_FUNIL.NEW_DEAL.CANCEL')"
          variant="faded"
          color="slate"
          type="reset"
          class="w-full"
          @click="closeDialog"
        />
        <Button
          :label="t('CRM_FUNIL.NEW_DEAL.SUBMIT')"
          color="blue"
          type="submit"
          class="w-full"
          :is-loading="isCreating"
          :disabled="isCreating"
        />
      </div>
    </template>
  </Dialog>
</template>
